import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Activity, CalendarX2, Home, MoreHorizontal } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import "../components/touch/touchScale.css";
import { useBootstrap } from "../api/queries/useBootstrap";
import { BrandMark } from "./BrandMark";
import { EnvironmentBadge } from "./EnvironmentBadge";
import { ScopeSwitcher } from "./ScopeSwitcher";
import { NavSheet } from "./NavSheet";
import { useLabels } from "./useLabels";
import { useCurrentPath } from "./useRouteTitle";

/** The four fixed tabs. "More" opens the whole config in `NavSheet`. */
const TABS: { path: string; icon: LucideIcon; labelKey?: "planningRuns" | "closures" }[] = [
  { path: "/", icon: Home },
  { path: "/runs", icon: Activity, labelKey: "planningRuns" },
  { path: "/calendars", icon: CalendarX2, labelKey: "closures" },
];

export function AppShellPhone({ children }: { children: ReactNode }) {
  const { data } = useBootstrap();
  const labels = useLabels();
  const current = useCurrentPath();
  const [moreOpen, setMoreOpen] = useState(false);

  const tabClass = (active: boolean) =>
    `type-body flex min-h-11 flex-1 flex-col items-center justify-center gap-[2px] ${
      active ? "text-primary" : "text-muted-foreground"
    }`;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-10 flex h-12 items-center gap-xs border-b border-border bg-background px-md">
        <BrandMark className="h-4 w-6 text-primary" />
        <span className="type-body truncate text-foreground">{data?.tenant.name}</span>
        <EnvironmentBadge />
        <div className="flex-1" />
        <ScopeSwitcher />
      </header>

      <main className="touch-scale flex-1 pb-[calc(4rem+env(safe-area-inset-bottom))]">
        {children}
      </main>

      <nav
        aria-label="Main"
        className="touch-scale fixed inset-x-0 bottom-0 z-20 flex border-t border-border bg-background pb-[env(safe-area-inset-bottom)]"
      >
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = tab.path === "/" ? current === "/" : current.startsWith(tab.path);
          const text = tab.labelKey ? labels[tab.labelKey] : "Home";
          return (
            <Link key={tab.path} to={tab.path} className={tabClass(active)}>
              <Icon className="size-5" />
              <span className="type-body leading-none">{text}</span>
            </Link>
          );
        })}
        <button type="button" onClick={() => setMoreOpen(true)} className={tabClass(moreOpen)}>
          <MoreHorizontal className="size-5" />
          <span className="type-body leading-none">More</span>
        </button>
      </nav>

      <NavSheet open={moreOpen} onOpenChange={setMoreOpen} />
    </div>
  );
}
